/**
 * Pure, React-free helpers for the Daily Update domain: id generation, the
 * local "today" date, blank/edit draft factories, validation (a useful update
 * must say SOMETHING), feed sorting, the day → engineer grouping the feed
 * relies on, blocker detection, and friendly date formatting.
 *
 * AUTO-COLLECT extension point (not available yet): when Jira / repo / deploy
 * integrations land, an upstream collector would produce candidate updates
 * (e.g. "moved STORY-123 to In Review", "deployed X") that AI-STRUCTURE turns
 * into a {@link DailyUpdateDraft} for a human to VERIFY in the composer before
 * posting — the TEAM-CONSUME feed below is unchanged. Until then, updates are
 * entered manually.
 *
 * Deliberately NO productivity/velocity/commit-count/leaderboard metric: the
 * feed is grouped for AWARENESS (who did/said what), never counted or ranked.
 */

import { v4 as uuid } from 'uuid';
import type {
  DailyUpdate,
  DailyUpdateDraft,
  DailyUpdateFilter,
  DailyUpdateSummary,
} from './types';

/** Deterministic document id for a new daily update. */
export function dailyUpdateDocId(): string {
  return `du_${uuid()}`;
}

/** The local calendar date as `yyyy-mm-dd`. */
export function todayIso(): string {
  const d = new Date();
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/** Creates an empty draft for the composer (seeds engineer + today + optional story). */
export function blankDailyUpdateDraft(
  engineerAlias = '',
  seed: Partial<DailyUpdateDraft> = {},
): DailyUpdateDraft {
  return {
    engineer: engineerAlias,
    date: todayIso(),
    storyId: undefined,
    workstreamId: undefined,
    goalId: undefined,
    yesterday: '',
    today: '',
    resultLearning: '',
    blocker: '',
    ...seed,
  };
}

/** Projects an existing update into an editable draft. */
export function draftFromDailyUpdate(update: DailyUpdate): DailyUpdateDraft {
  return {
    engineer: update.engineer,
    date: update.date,
    storyId: update.storyId,
    workstreamId: update.workstreamId,
    goalId: update.goalId,
    yesterday: update.yesterday ?? '',
    today: update.today ?? '',
    resultLearning: update.resultLearning ?? '',
    blocker: update.blocker ?? '',
  };
}

/**
 * Validates a draft for posting. An update is only useful if it says something,
 * so at least one of today / yesterday / result / blocker must be non-empty
 * (alongside an engineer + date).
 */
export function validateDailyUpdateDraft(draft: DailyUpdateDraft): string[] {
  const problems: string[] = [];
  if (!draft.engineer.trim()) problems.push('An engineer is required.');
  if (!draft.date.trim()) problems.push('A date is required.');
  const hasContent =
    !!draft.today?.trim() ||
    !!draft.yesterday?.trim() ||
    !!draft.resultLearning?.trim() ||
    !!draft.blocker?.trim();
  if (!hasContent) {
    problems.push('Add a "today", result, or blocker — an empty update helps no one.');
  }
  return problems;
}

/** True when this update carries a free-text blocker worth converting. */
export function hasBlocker(
  update: Pick<DailyUpdate, 'blocker'> | Pick<DailyUpdateSummary, 'blocker'>,
): boolean {
  return !!update.blocker?.trim();
}

/** Applies an engineer filter (empty field matches everything). */
export function filterDailyUpdates(
  items: DailyUpdateSummary[],
  filter: DailyUpdateFilter,
): DailyUpdateSummary[] {
  const engineer = filter.engineer.trim();
  return items.filter((u) => {
    if (engineer && u.engineer !== engineer) return false;
    return true;
  });
}

/** Unique, sorted engineer aliases present across the given updates. */
export function collectEngineers(items: DailyUpdateSummary[]): string[] {
  const set = new Set<string>();
  for (const u of items) {
    const t = (u.engineer ?? '').trim();
    if (t) set.add(t);
  }
  return Array.from(set).sort((a, b) => a.localeCompare(b));
}

/** Sorts updates newest-first (by createdAt, then date). */
export function sortByRecency(
  items: DailyUpdateSummary[],
): DailyUpdateSummary[] {
  return items
    .slice()
    .sort((a, b) => {
      const byCreated = (b.createdAt ?? '').localeCompare(a.createdAt ?? '');
      if (byCreated !== 0) return byCreated;
      return (b.date ?? '').localeCompare(a.date ?? '');
    });
}

/** A per-engineer bucket of a day's updates. */
export interface DailyUpdateEngineerGroup {
  engineer: string;
  updates: DailyUpdateSummary[];
}

/** A day's updates, bucketed by engineer. */
export interface DailyUpdateDayGroup {
  /** yyyy-mm-dd key. */
  date: string;
  /** Friendly label (Today / Yesterday / "Mon, Jul 21"). */
  label: string;
  /** Engineers who posted that day (alphabetical), each with their updates. */
  engineers: DailyUpdateEngineerGroup[];
  /** Total updates that day. */
  count: number;
}

/**
 * Groups updates by day (most-recent day first), then by engineer
 * (alphabetical) within a day; each engineer's updates keep newest-first order.
 */
export function groupByDayEngineer(
  items: DailyUpdateSummary[],
): DailyUpdateDayGroup[] {
  const sorted = sortByRecency(items);
  const byDay = new Map<string, DailyUpdateSummary[]>();
  for (const u of sorted) {
    const key = u.date || u.createdAt.slice(0, 10) || '—';
    const list = byDay.get(key) ?? [];
    list.push(u);
    byDay.set(key, list);
  }
  return Array.from(byDay.keys())
    .sort((a, b) => b.localeCompare(a))
    .map((date) => {
      const dayItems = byDay.get(date) ?? [];
      const byEngineer = new Map<string, DailyUpdateSummary[]>();
      for (const u of dayItems) {
        const e = u.engineer || '—';
        const list = byEngineer.get(e) ?? [];
        list.push(u);
        byEngineer.set(e, list);
      }
      const engineers: DailyUpdateEngineerGroup[] = Array.from(byEngineer.keys())
        .sort((a, b) => a.localeCompare(b))
        .map((engineer) => ({ engineer, updates: byEngineer.get(engineer) ?? [] }));
      return { date, label: formatDay(date), engineers, count: dayItems.length };
    });
}

/** Formats a yyyy-mm-dd day as Today / Yesterday / a friendly weekday date. */
export function formatDay(date: string): string {
  if (!date) return '';
  const d = new Date(`${date}T00:00:00`);
  if (Number.isNaN(d.getTime())) return date;
  if (date === todayIso()) return 'Today';
  const yesterday = new Date();
  yesterday.setDate(yesterday.getDate() - 1);
  const yIso = `${yesterday.getFullYear()}-${String(yesterday.getMonth() + 1).padStart(2, '0')}-${String(yesterday.getDate()).padStart(2, '0')}`;
  if (date === yIso) return 'Yesterday';
  return d.toLocaleDateString(undefined, {
    weekday: 'short',
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

/** Formats an ISO timestamp as a compact local time (for a card's posted-at). */
export function formatTime(iso?: string): string {
  if (!iso) return '';
  const d = new Date(iso);
  if (Number.isNaN(d.getTime())) return '';
  return d.toLocaleTimeString(undefined, { hour: 'numeric', minute: '2-digit' });
}
